import React, { useEffect, useState } from "react";
import { X, Share, PlusSquare, Download } from "lucide-react";
import { isAndroidWebView, isStandalonePwa, isIosSafariBrowserTab } from "../lib/platformDetect";
import { usePublicSettings } from "../hooks/usePublicSettings";

const IOS_DISMISS_KEY = "vj_pwa_ios_guide_dismissed";
const ANDROID_DISMISS_KEY = "vj_pwa_android_prompt_dismissed_at";
const ANDROID_DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

// 2026-08-30 - Global "Install App" experience for browser/PWA visitors only. Never rendered
// inside the Android WebView (native app already has its own install/APK flow) and never
// re-shown once the app is already running as an installed PWA (requirement #12).
export function InstallPwaPrompt() {
  const { data: publicSettings } = usePublicSettings();
  const pwaSettings = publicSettings?.pwa || null;
  const [deferredEvent, setDeferredEvent] = useState(null);
  const [showAndroidBanner, setShowAndroidBanner] = useState(false);
  const [showIosBanner, setShowIosBanner] = useState(false);

  useEffect(() => {
    if (!pwaSettings?.enabled || isAndroidWebView() || isStandalonePwa()) return;

    if (pwaSettings.android_prompt_enabled) {
      const lastDismissed = Number(localStorage.getItem(ANDROID_DISMISS_KEY) || 0);
      const onBeforeInstall = (event) => {
        event.preventDefault();
        setDeferredEvent(event);
        if (Date.now() - lastDismissed > ANDROID_DISMISS_COOLDOWN_MS) setShowAndroidBanner(true);
      };
      window.addEventListener("beforeinstallprompt", onBeforeInstall);
      return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
    }
  }, [pwaSettings]);

  useEffect(() => {
    if (!pwaSettings?.enabled || !pwaSettings.ios_guide_enabled) return;
    if (isAndroidWebView() || isStandalonePwa() || !isIosSafariBrowserTab()) return;
    if (localStorage.getItem(IOS_DISMISS_KEY) === "1") return;
    setShowIosBanner(true);
  }, [pwaSettings]);

  const handleAndroidInstall = async () => {
    if (!deferredEvent) return;
    try {
      await deferredEvent.prompt();
    } catch {
      // ignore - browser may reject if user already interacted differently
    }
    setShowAndroidBanner(false);
    setDeferredEvent(null);
  };

  const dismissAndroid = () => {
    localStorage.setItem(ANDROID_DISMISS_KEY, String(Date.now()));
    setShowAndroidBanner(false);
  };

  const dismissIos = () => {
    localStorage.setItem(IOS_DISMISS_KEY, "1");
    setShowIosBanner(false);
  };

  if (showAndroidBanner) {
    return (
      <div
        data-testid="pwa-install-banner-android"
        className="fixed bottom-0 left-0 right-0 z-[70] flex items-center gap-3 bg-[#12142080] backdrop-blur-xl border-t border-white/10 px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]"
      >
        <div className="w-9 h-9 rounded-lg bg-primary/20 flex items-center justify-center shrink-0">
          <Download size={18} className="text-primary-light" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white truncate">Install {pwaSettings?.short_name || "VJ Global"}</p>
          <p className="text-xs text-slate-400 truncate">Add to your Home Screen for faster, app-like access.</p>
        </div>
        <button
          type="button" onClick={handleAndroidInstall} data-testid="pwa-install-button"
          className="shrink-0 rounded-full bg-primary text-white text-xs font-bold px-4 py-2"
        >
          Install
        </button>
        <button type="button" onClick={dismissAndroid} data-testid="pwa-install-dismiss-android" className="shrink-0 text-slate-500 hover:text-slate-300 p-1">
          <X size={16} />
        </button>
      </div>
    );
  }

  if (showIosBanner) {
    const guideText = pwaSettings?.install_guide_text || "";
    return (
      <div
        data-testid="pwa-install-banner-ios"
        className="fixed bottom-0 left-0 right-0 z-[70] bg-[#12142080] backdrop-blur-xl border-t border-white/10 px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]"
      >
        <div className="flex items-start gap-3">
          <div className="flex items-center gap-1.5 shrink-0 mt-0.5">
            <Share size={16} className="text-primary-light" />
            <PlusSquare size={16} className="text-primary-light" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white mb-0.5">Install {pwaSettings?.short_name || "VJ Global"} on your iPhone</p>
            <p className="text-xs text-slate-400 whitespace-pre-line">{guideText}</p>
          </div>
          <button type="button" onClick={dismissIos} data-testid="pwa-install-dismiss-ios" className="shrink-0 text-slate-500 hover:text-slate-300 p-1">
            <X size={16} />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
